import { Set } from 'immutable';
import getIn from 'lodash/get';

import { getAllAnnotations } from '../../../lib/EntityUtils';
import { getPlaylistFromState } from '../../../lib/MusicUtils';

import * as D from './deps';

// Used for either creating or editing playlist details
// - adding tracks (itemPandoraIds) is only allowed when creating
export const detailsDto = (name, description, secret, isPrivate, itemPandoraIds) => {
  return itemPandoraIds
    ? { name, description, secret, private: isPrivate, itemPandoraIds }
    : { name, description, secret, private: isPrivate };
};

export const insertTrackDto = (trackPandoraId, newIndex) => {
  return { trackPandoraId, newIndex };
};

export const unlockedPlaylistDto = () => {
  return { unlocked: true };
};

export const moveTrackDto = (itemId, oldIndex, newIndex) => {
  return { itemId, oldIndex, newIndex };
};

export const createPlaylistTrackMoves = (oldTrackIndex, newTrackIndex, tracks) => {
  const moves = [];
  const failureMessage = `createPlaylistTrackMoves failed with indices: ${oldTrackIndex} and ${newTrackIndex} with list size: ${tracks.size}`;
  if (tracks && tracks.has(oldTrackIndex)) {
    moves.push(moveTrackDto(tracks.get(oldTrackIndex).get('itemId'), oldTrackIndex, newTrackIndex));
    const incr = oldTrackIndex > newTrackIndex ? -1 : 1;
    for (let i = oldTrackIndex; i !== newTrackIndex; i += incr) {
      if (tracks.has(i + incr)) {
        const track = tracks.get(i + incr);
        moves.push(moveTrackDto(track.get('itemId'), i + incr, i));
      } else {
        throw new Error(failureMessage);
      }
    }
  } else {
    throw new Error(failureMessage);
  }
  return moves;
};

export const actionsMatch = (attemptAction, endAction) => {
  const attemptTrackToken = attemptAction.payload.trackToken;
  const attemptThumbUp = attemptAction.payload.isPositive;
  let endTrackToken = getIn(endAction, ['params', 'content', 'trackToken'], null);
  let endThumbUp = getIn(endAction, ['params', 'content', 'isPositive'], null);
  if (endAction.type === D.Constants.Api.ApiActions.ACTION_THUMB_UP_SUCCESS) {
    endTrackToken = getIn(endAction, ['params', 'content', 'pandoraId'], null);
    endThumbUp = true;
  } else if (endAction.type === D.Constants.Api.ApiActions.ACTION_THUMB_DOWN_SUCCESS) {
    endTrackToken = getIn(endAction, ['params', 'content', 'pandoraId'], null);
    endThumbUp = false;
  } else if (endAction.type === D.Constants.Api.ApiActions.ACTION_REMOVE_THUMB_SUCCESS) {
    endTrackToken = getIn(endAction, ['params', 'content', 'pandoraId'], null);
    endThumbUp = getIn(endAction, ['params', 'isPositive'], null);
  }
  return attemptTrackToken === endTrackToken && attemptThumbUp === endThumbUp;
};

export function getPlaylistFetchingOffsets(state, pandoraId) {
  return state.playlistT3.getIn([pandoraId, 'GET_TRACKS_OFFSETS_FETCHING']) || Set();
}

export function getPlaylistFetchedOffsets(state, pandoraId) {
  return state.playlistT3.getIn([pandoraId, 'GET_TRACKS_OFFSETS_FETCHED']) || Set();
}

export function getFeedbackFetchingOffsets(state, stationId) {
  return state.feedbackT3.getIn([stationId, 'FETCHING_OFFSETS']) || Set();
}

export function getFeedbackFetchedOffsets(state, stationId) {
  return state.feedbackT3.getIn([stationId, 'FETCHED_OFFSETS']) || Set();
}

export function getPlaylistOwnerInfo(state, playlistId, currentListenerId) {
  const annotations = getAllAnnotations(state);
  const thisPlaylist = getPlaylistFromState(state, playlistId) || annotations.get(playlistId);
  const listenerPandoraId = thisPlaylist.getIn(
    ['listenerIdInfo', 'listenerPandoraId'],
    thisPlaylist.get('listenerPandoraId'),
  );
  const listenerId = thisPlaylist.getIn(
    ['listenerIdInfo', 'listenerId'],
    thisPlaylist.get('listenerId'),
  );
  const isCurrentListener = thisPlaylist.getIn(['listenerIdInfo', 'isMe'], false);

  let owner = annotations.get(listenerPandoraId);
  if (!owner) {
    owner = state.entities.getIn(['playlists', listenerPandoraId]);
  }
  const ownerWebname = owner && owner.get('webname');
  const ownerName = owner && (owner.get('displayname') || ownerWebname);
  const isOwnedPlaylist =
    isCurrentListener || (listenerId && currentListenerId === listenerId.toString());
  return { ownerName, ownerWebname, isOwnedPlaylist };
}

export function isPlaylistEditable(playlist) {
  return playlist.getIn(['viewerInfo', 'editable']);
}

export function isPlaylistCollectible(state, playlistId) {
  const annotations = getAllAnnotations(state);
  const thisPlaylist = getPlaylistFromState(state, playlistId) || annotations.get(playlistId);
  return thisPlaylist.get('collectible');
}

// The external utils
export default {
  getPlaylistFetchingOffsets,
  getPlaylistFetchedOffsets,
  getFeedbackFetchingOffsets,
  getFeedbackFetchedOffsets,
  getPlaylistOwnerInfo,
  isPlaylistEditable,
  isPlaylistCollectible,
};
